import { alpha, Box, Collapse, Link, Stack, Typography, useTheme } from '@mui/material';
import { buildFriendlyConversationId } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes/dist/paths';
import { format, parseISO } from 'date-fns';
import { ArrowUpRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components';
import { ConversationTooltipContent } from 'src/components/SidePanelTabs/RecentConversations/conversation-info';
import { ConversationTooltip } from 'src/components/SidePanelTabs/RecentConversations/conversation-tooltip';
import {
  TOOLTIP_ENTER_DELAY,
  TOOLTIP_LEAVE_DELAY,
  useConversationTooltip,
} from 'src/components/SidePanelTabs/RecentConversations/use-conversation-tooltip';
import {
  type ConversationRecurrence,
  parseConversationRecurrence,
} from 'src/graphql/conversation-summary/parse-conversation-recurrence';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import {
  referenceIdSx,
  referenceRowSx,
  referenceSubjectSx,
} from 'src/pages/Sites/SiteOverview/reasoning-popover.styles';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';

type PriorConversation = ConversationRecurrence['priorConversations'][number];

const ordinal = (n: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const lastTwo = n % 100;
  return `${n}${suffixes[(lastTwo - 20) % 10] || suffixes[lastTwo] || suffixes[0]}`;
};

const shortDate = (date: string) => format(parseISO(date), 'MMM d');

interface TicketLinkProps {
  conversationId: string;
  workspaceConversationId: number;
  partnerId: number;
  onHover: (conversationId: string, anchor: HTMLElement) => void;
  onLeave: () => void;
}

const TicketLink = ({
  conversationId,
  workspaceConversationId,
  partnerId,
  onHover,
  onLeave,
}: TicketLinkProps) => {
  const { navigate } = useNavigateRoute();
  const friendlyId = buildFriendlyConversationId(partnerId, workspaceConversationId);

  return (
    <Link
      component="button"
      underline="hover"
      onMouseEnter={(event) => onHover(conversationId, event.currentTarget)}
      onMouseLeave={onLeave}
      onFocus={(event) => onHover(conversationId, event.currentTarget)}
      onBlur={onLeave}
      onClick={() => navigate(portalPaths.conversation.full(friendlyId))}
      sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
    >
      {friendlyId}
    </Link>
  );
};

interface OtherOccurrenceRowProps
  extends Omit<TicketLinkProps, 'conversationId' | 'workspaceConversationId'> {
  prior: PriorConversation;
}

/** One earlier ticket as a site overview reference row; the list's subgrid keeps the columns aligned. */
const OtherOccurrenceRow = ({
  prior,
  partnerId,
  onHover,
  onLeave,
}: OtherOccurrenceRowProps) => {
  const { navigate } = useNavigateRoute();
  const friendlyId = buildFriendlyConversationId(partnerId, prior.workspaceConversationId);

  return (
    <Box
      component="button"
      type="button"
      onMouseEnter={(event) => onHover(prior.conversationId, event.currentTarget)}
      onMouseLeave={onLeave}
      onFocus={(event) => onHover(prior.conversationId, event.currentTarget)}
      onBlur={onLeave}
      onClick={() => navigate(portalPaths.conversation.full(friendlyId))}
      sx={[referenceRowSx, { gridColumn: '1 / -1', gridTemplateColumns: 'subgrid' }]}
    >
      <Box component="span" sx={[referenceIdSx, { fontFamily: 'inherit' }]}>
        {shortDate(prior.date)}
      </Box>
      <Box component="span" data-citation-id sx={referenceIdSx}>
        {friendlyId}
      </Box>
      <Box component="span" data-citation-subject sx={referenceSubjectSx}>
        {prior.issue || prior.resolution}
      </Box>
      <ArrowUpRight />
    </Box>
  );
};

/** Rendered only when there is a flag, so the earlier tickets are fetched only then. */
const RecurringIssueNotice = ({
  recurrence,
  partnerId,
}: {
  recurrence: ConversationRecurrence;
  partnerId: number;
}) => {
  const theme = useTheme();
  const tooltip = useConversationTooltip();
  const [showOthers, setShowOthers] = useState(false);
  const { priorConversations } = recurrence;

  const priorIds = priorConversations.map(({ conversationId }) => conversationId).join(',');
  const filter = useMemo<ConditionInput[]>(
    () => [{ attribute: 'conversationId', operator: 'in', value: priorIds }],
    [priorIds],
  );
  const { records } =
    useSearch({
      entityName: EntityNames.Conversation,
      searchTerm: '',
      showResultsWhenEmptySearchTerm: true,
      filter,
      limit: priorConversations.length,
    }) ?? {};
  const recordsById = useMemo(
    () => new Map((records ?? []).map((record) => [record.conversationId, record])),
    [records],
  );

  // the fix agents need is the latest recorded one, and only on a ticket the model judged the same issue
  const lastFixed = priorConversations.find((prior) => prior.cited && prior.fixed);
  const highlighted = lastFixed ?? priorConversations[0];
  const others = priorConversations.filter((prior) => prior !== highlighted);
  const accent = theme.palette.warning.main;
  const secondary = theme.palette.custom.OV.sys.onSurfaceVariant;

  const { open: openTooltip } = tooltip;
  const onHover = useCallback(
    (conversationId: string, anchor: HTMLElement) =>
      openTooltip(recordsById.get(conversationId), anchor),
    [openTooltip, recordsById],
  );

  const link = ({ conversationId, workspaceConversationId }: PriorConversation) => (
    <TicketLink
      conversationId={conversationId}
      workspaceConversationId={workspaceConversationId}
      partnerId={partnerId}
      onHover={onHover}
      onLeave={tooltip.close}
    />
  );

  return (
    <ConversationTooltip
      anchorEl={tooltip.anchorEl}
      enterDelay={TOOLTIP_ENTER_DELAY}
      isOpen={tooltip.isVisible}
      leaveDelay={TOOLTIP_LEAVE_DELAY}
      title={
        tooltip.conversation ? (
          <ConversationTooltipContent conversation={tooltip.conversation} />
        ) : (
          ''
        )
      }
    >
      <Box
        data-testid="conversation-recurring-issue"
        sx={{
          borderLeft: `2px solid ${accent}`,
          backgroundColor: alpha(accent, 0.08),
          borderRadius: theme.spacing(0.5),
          padding: theme.spacing(1, 1.5),
          margin: theme.spacing(0.5, 0, 1),
          fontSize: theme.typography.fontSize,
          lineHeight: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <DynamicIcon name="repeat" library="lucide" size={14} style={{ color: accent }} />
          <Typography fontSize="inherit" fontWeight={600}>
            Recurring issue
          </Typography>
          <Typography fontSize="0.75rem" color={secondary}>
            · {ordinal(priorConversations.length + 1)} time in 12 months
          </Typography>
        </Stack>

        <Typography fontSize="inherit" sx={{ mt: 0.5 }}>
          {lastFixed ? 'Last fixed' : 'Last seen'} {shortDate(highlighted.date)} (
          {link(highlighted)}
          ): {highlighted.resolution || highlighted.issue}
        </Typography>

        {/* alert-driven tickets can repeat dozens of times, so the full list waits behind a toggle */}
        {others.length ? (
          <>
            <Link
              component="button"
              underline="hover"
              aria-expanded={showOthers}
              onClick={() => setShowOthers((shown) => !shown)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                mt: 0.5,
                fontSize: '0.75rem',
                color: secondary,
              }}
            >
              {showOthers
                ? 'Hide other times'
                : `Show ${others.length} other ${others.length === 1 ? 'time' : 'times'}`}
              <DynamicIcon
                name={showOthers ? 'chevron-up' : 'chevron-down'}
                library="lucide"
                size={12}
              />
            </Link>
            <Collapse in={showOthers}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto minmax(0,1fr) 0.75rem',
                  mt: 0.25,
                  mx: -1,
                }}
              >
                {others.map((prior) => (
                  <OtherOccurrenceRow
                    key={prior.conversationId}
                    prior={prior}
                    partnerId={partnerId}
                    onHover={onHover}
                    onLeave={tooltip.close}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        ) : undefined}
      </Box>
    </ConversationTooltip>
  );
};

interface RecurringIssueProps {
  intermediateSummary?: string | null;
  partnerId: number;
}

export const RecurringIssue = ({ intermediateSummary, partnerId }: RecurringIssueProps) => {
  const recurrence = useMemo(
    () => parseConversationRecurrence(intermediateSummary),
    [intermediateSummary],
  );

  return recurrence ? (
    <RecurringIssueNotice recurrence={recurrence} partnerId={partnerId} />
  ) : null;
};
